import { BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH } from "common/constants";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { dateToMoment } from "@shared/utils/date";
import { localStorageService } from "utils/localStorageService";
import Banner from "common/Banner/Banner";
import { AuthUser } from "@shared/types";
import { useRestrictedCountriesPurchase } from "utils/hooks/misc";
import Link from "common/Typography/Link";

interface SubscriptionEndsBannerProps {
  user: AuthUser;
}

const SubscriptionEndsBanner = ({ user }: SubscriptionEndsBannerProps) => {
  const { onPurchaseClick } = useRestrictedCountriesPurchase();
  const [reminderThresholdMatch, setReminderThresholdMatch] = useState<
    number | undefined
  >();
  const userExpiration = dateToMoment(user.expiration_timestamp, true);
  const subscriptionDaysLeft = moment
    .duration(userExpiration.diff(moment().local()))
    .asDays();

  const getLocalStorageKey = (threshold: number) =>
    `hide_subscription_ends_${threshold}`;
  const getThresholdAlreadyHidden = (threshold: number) =>
    Boolean(localStorageService.getValue(getLocalStorageKey(threshold)));

  useEffect(() => {
    if (subscriptionDaysLeft < 0) return;
    let thresholdMatch: number | undefined;
    REMINDER_THRESHOLDS.sort().some((threshold) => {
      const isAlreadyHidden = getThresholdAlreadyHidden(threshold);
      if (subscriptionDaysLeft <= threshold) {
        if (isAlreadyHidden) {
          return;
        } else {
          thresholdMatch = threshold;
        }
      }
      return thresholdMatch;
    });
    if (thresholdMatch) setReminderThresholdMatch(thresholdMatch);
  }, []);

  const onCloseClick = () => {
    if (reminderThresholdMatch) {
      localStorageService.setValue(
        getLocalStorageKey(reminderThresholdMatch),
        1
      );
      setReminderThresholdMatch(undefined);
    }
  };

  if (!reminderThresholdMatch) return null;

  return (
    <Banner onCloseClick={onCloseClick}>
      Your {user.is_trial ? "trial " : ""} subscription will end in{" "}
      {Math.ceil(subscriptionDaysLeft)} days.{" "}
      <Link
        onClick={() =>
          onPurchaseClick(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH)
        }
      >
        Click here
      </Link>{" "}
      to extend your subscription.
    </Banner>
  );
};

export default SubscriptionEndsBanner;

const REMINDER_THRESHOLDS = [1, 7];
